import { Box, CircularProgress, Modal } from "@mui/material";
import React, { useMemo, useRef, useState } from "react";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/20/solid";
import { ClockIcon, PrinterIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { getToken, revertcomplimentaryCart, updateRestaurantCartProductStatus } from "../../../../../../config/Services";
import { useSnackbar } from "../../../hooks/SnackbarProvider";
import RecallOrderModal from "../RecallOrderModal/RecallOrderModal";
import vegIcon from "../../../../../../assets/images/veg-new.png";
import nonVegIcon from "../../../../../../assets/images/non-veg-new.png";
import { KitchenClockProvider } from "../../../../KDS/components/TimedOrderCard";
import { formatPrepEstimate } from "../../../../KDS/orderTiming";
import useKotKitchenJobs from "./useKotKitchenJobs";
import { buildKotKitchenMatches, getKotItemKitchenTiming } from "./kotKitchenTiming";
import KotItemKitchenProgress from "./KotItemKitchenProgress";
import { getPlacedKotGroups } from "./kotGroups";
import { getRevertedComplimentaryCart, getComplimentaryRevertError } from "./complimentaryRevert";
import { getConfirmedServedProduct, getServeError } from "./serveKotItem";
import "./AllKotsModal.css";

const AllKotsModal = ({
  open,
  onClose,
  groupedCartItems,
  cartId,
  orgId,
  locId,
  generatedBy,
  generatedByName,
  printToken,
  customerName,
  setShowFineDineActionButtons,
  setHasRequestedForNewItem,
  onComplimentary,
  onCartUpdated,
  onItemServed,
  complementaryButtonLoader,
  onDiscount,
  onRepeatOrder,
}) => {
  const showSnackbar = useSnackbar();
  const [expandedGroups, setExpandedGroups] = useState({});
  const [recallModalData, setRecallModalData] = useState(null);
  const [repeatLoadingId, setRepeatLoadingId] = useState(null);
  const [reprintLoadingSeq, setReprintLoadingSeq] = useState(null);
  const [isListView, setIsListView] = useState(false);
  const [complimentaryLoadingId, setComplimentaryLoadingId] = useState(null);
  const [revertLoadingId, setRevertLoadingId] = useState(null);
  const [revertedProducts, setRevertedProducts] = useState(null);
  const revertRequestRef = useRef(null);
  const serveRequestRef = useRef(null);
  const [serveLoadingId, setServeLoadingId] = useState(null);
  const [servedIds, setServedIds] = useState(() => new Set());
  const contextKey = JSON.stringify([orgId, locId, cartId]);
  const contextRef = useRef(contextKey);
  contextRef.current = contextKey;

  React.useEffect(() => {
    setRevertedProducts(null);
    setServedIds(new Set());
  }, [groupedCartItems, contextKey]);
  React.useEffect(() => {
    revertRequestRef.current = null;
    setRevertLoadingId(null);
    serveRequestRef.current = null;
    setServeLoadingId(null);
  }, [contextKey]);
  React.useEffect(() => () => {
    contextRef.current = null;
    revertRequestRef.current = null;
    serveRequestRef.current = null;
  }, []);

  React.useEffect(() => {
    if (!complementaryButtonLoader) setComplimentaryLoadingId(null);
  }, [complementaryButtonLoader]);

  const appConfig = (() => {
    try { return JSON.parse(localStorage.getItem("appConfig")) || {}; }
    catch { return {}; }
  })();
  const isKDSRequired = appConfig?.settings?.DISPLAY_SYSTEM_MODE?.kdsDisplayScreens?.includes("KDS");

  const kotGroups = useMemo(
    () => getPlacedKotGroups(groupedCartItems).map((group) => ({
      ...group,
      items: group?.items?.map((item) => {
        const current = revertedProducts?.find((product) => product?.cartProductId === item?.cartProductId) || item;
        return servedIds.has(String(current?.cartProductId)) && current?.productStatus !== "RETURNED"
          ? { ...current, productStatus: "SERVED" } : current;
      }),
    })),
    [groupedCartItems, revertedProducts, servedIds]
  );

  const handleRemoveComplimentary = async (item) => {
    if (revertRequestRef.current || serveRequestRef.current || complementaryButtonLoader) return;
    const quantity = Number(item?.quantity);
    if (!cartId || !item?.cartProductId || !Number.isFinite(quantity) || quantity <= 0) {
      showSnackbar("Unable to remove complimentary: cart, product or quantity is missing.", "error");
      return;
    }
    if (!generatedBy) {
      showSnackbar("Unable to identify the current user. Please reload and try again.", "error");
      return;
    }
    const request = { contextKey };
    revertRequestRef.current = request;
    setRevertLoadingId(item?.cartProductId);
    try {
      const response = await revertcomplimentaryCart({
        cartId, cartProductId: item?.cartProductId, quantity,
        updatedBy: generatedBy, updatedByName: generatedByName || "",
      });
      if (contextRef.current !== request.contextKey) return;
      const updatedCart = getRevertedComplimentaryCart(response, cartId, item?.cartProductId);
      setRevertedProducts(updatedCart?.cartProducts);
      try {
        onCartUpdated?.(updatedCart);
        showSnackbar("Complimentary removed successfully", "success");
      } catch {
        showSnackbar("Complimentary removed, but cart totals could not update. Please refresh the cart.", "warning");
      }
    } catch (error) {
      if (contextRef.current === request.contextKey) showSnackbar(getComplimentaryRevertError(error), "error");
    } finally {
      if (revertRequestRef.current === request) {
        revertRequestRef.current = null;
        setRevertLoadingId(null);
      }
    }
  };

  const grandTotal = useMemo(() => {
    let total = 0;
    kotGroups.forEach((group) =>
      group?.items?.forEach((item) => {
        if (item?.productStatus !== "RETURNED")
          total += parseFloat(item?.subTotal || 0);
      })
    );
    return total;
  }, [kotGroups]);

  const aggregatedKotItems = useMemo(() => {
    const aggregated = {};
    kotGroups.forEach((group) =>
      group.items.forEach((item) => {
        const addonKey = item?.addonProduct
          ?.map((a) => `${a?.variantId}:${a?.quantity || a?.qty || 1}`).sort().join("-") || "no-addon";
        const isReturned = item?.productStatus === "RETURNED";
        const isComplimentary = item?.inventoryStatus === "COMPLEMENTARY";
        const statusKey = isReturned ? "returned" : isComplimentary ? "complimentary" : "normal";
        const key = `${item?.variantId}-${addonKey}-${statusKey}`;
        if (!aggregated[key]) {
          aggregated[key] = {
            variantId: item?.variantId,
            productName: item?.productName,
            variantName: item?.variantName,
            foodType: item?.foodType,
            quantity: 0,
            subTotal: 0,
            originalTotal: 0,
            addonProduct: item?.addonProduct || [],
            isReturned,
            isComplimentary,
            sequences: [],
          };
        }
        const entry = aggregated[key];
        entry.quantity += item?.quantity || 0;
        entry.subTotal += parseFloat(item?.subTotal || 0);
        entry.originalTotal += parseFloat(item?.sellingPrice ?? item?.subTotal ?? 0) || 0;
        if (!entry.sequences.includes(group?.sequence)) entry.sequences.push(group?.sequence);
      })
    );
    return Object.values(aggregated);
  }, [kotGroups]);

  const isExpanded = (idx) => expandedGroups[idx] !== false;
  const toggleGroup = (idx) => setExpandedGroups((prev) => ({ ...prev, [idx]: !isExpanded(idx) }));

  const handleRepeatOrder = async (ele) => {
    if (repeatLoadingId || !onRepeatOrder) return;
    setRepeatLoadingId(ele?.cartProductId);
    try {
      await onRepeatOrder(ele?.cartProductId);
      showSnackbar("Order repeated successfully", "success");
    } catch (err) {
      showSnackbar(err?.response?.data?.message || err?.message || "Failed to repeat order", "error");
    } finally {
      setRepeatLoadingId(null);
    }
  };

  const handleReprintKot = async (sequence) => {
    if (reprintLoadingSeq === sequence) return;
    const sendToKitchenEnabled = localStorage.getItem("sendToKitchen") === "true";
    setReprintLoadingSeq(sequence);
    try {
      const res = await getToken({
        cartId, orderSequence: sequence, generatedBy, generatedByName,
        ...((sendToKitchenEnabled || isKDSRequired) && { isSendToKitchen: true }),
        isPrintNeeded: sendToKitchenEnabled,
      });
      if (!sendToKitchenEnabled) {
        await printToken({
          tableId: res?.data?.data?.data?.tableId,
          tokenNumber: res?.data?.data?.data?.tokenNumber,
          tableNumber: res?.data?.data?.data?.tableNumber,
          subOrderType: res?.data?.data?.data?.subOrderType,
          customerName: customerName || "Walk-in Customer",
          orderItems: res?.data?.data?.data?.orderedProducts,
          comments: res?.data?.data?.data?.comments || "N/A",
          layoutName: res?.data?.data?.data?.layoutName,
        });
      }
      showSnackbar("KOT reprinted successfully", "success");
    } catch {
      showSnackbar("Failed to reprint KOT", "error");
    } finally {
      setReprintLoadingSeq(null);
    }
  };

  const { status: kitchenJobsStatus, jobs: kitchenJobs, error: kitchenJobsError, refresh: refreshKitchenJobs } =
    useKotKitchenJobs({ open, orgId, locId, cartId, groups: kotGroups });
  const kitchenMatches = useMemo(
    () => kitchenJobsStatus === "ready"
      ? buildKotKitchenMatches(kotGroups, kitchenJobs, cartId)
      : new Map(),
    [kotGroups, kitchenJobs, cartId, kitchenJobsStatus]
  );
  const getItemTiming = (item) => (
    <KotItemKitchenProgress
      item={item}
      matches={kitchenMatches.get(String(item?.cartProductId)) || []}
      loadStatus={kitchenJobsStatus}
    />
  );
  const getOrderedTimeText = (item) => getKotItemKitchenTiming(
    item, kitchenMatches.get(String(item?.cartProductId)) || [], Date.now(), kitchenJobsStatus
  ).orderedText;

  const isReadyToServe = (item) => getKotItemKitchenTiming(
    item, kitchenMatches.get(String(item?.cartProductId)) || [], Date.now(), kitchenJobsStatus
  )?.statusKey === "ready";

  const handleServe = async (item) => {
    if (serveRequestRef.current || revertRequestRef.current || complementaryButtonLoader ||
      item?.productStatus === "SERVED" || !isReadyToServe(item)) return;
    if (!cartId || !item?.cartProductId || !item?.variantId || !generatedBy) {
      showSnackbar("Unable to serve: cart, product, variant or current user is missing. Please refresh and try again.", "error");
      return;
    }
    const request = { contextKey };
    serveRequestRef.current = request;
    setServeLoadingId(item?.cartProductId);
    try {
      const response = await updateRestaurantCartProductStatus({
        cartId,
        cartProductId: item?.cartProductId,
        variantId: item?.variantId,
        cartProductStatus: "SERVED",
        comments: item?.comments || "",
        updatedById: generatedBy,
        updatedByName: generatedByName || "",
      });
      if (contextRef.current !== request.contextKey) return;
      const servedProduct = getConfirmedServedProduct(response, cartId, item?.cartProductId, item?.variantId);
      setServedIds((previous) => new Set(previous).add(String(servedProduct?.cartProductId)));
      try {
        onItemServed?.(servedProduct);
        showSnackbar("Item marked as served", "success");
      } catch {
        showSnackbar("Item served, but the cart could not update. Please refresh the cart.", "warning");
      }
    } catch (error) {
      if (contextRef.current === request.contextKey) showSnackbar(getServeError(error), "error");
    } finally {
      if (serveRequestRef.current === request) {
        serveRequestRef.current = null;
        setServeLoadingId(null);
      }
    }
  };

  const renderAddonText = (addons) => addons?.length > 0
    ? addons.map((a, i) => {
        const q = a?.quantity || a?.qty || 1;
        return (
          <React.Fragment key={i}>
            {i > 0 && ", "}
            {a?.itemName}
            {q > 1 && <b> (x{q})</b>}
          </React.Fragment>
        );
      })
    : null;

  const formatAmount = (value) => Number((value || 0).toFixed(2));

  const renderAggregatedCard = (item, key) => {
    const addonText = renderAddonText(item?.addonProduct);
    const kotLabel = `From ${item?.sequences?.map((seq) => `KOT-${seq}`).join(", ")}`;
    return (
      <div className={`akm-item-card ${item?.isReturned ? "recalled" : ""} ${item?.isComplimentary ? "complimentary" : ""}`} key={key}>
        <div className="akm-item-card-top">
          <div className="akm-item-card-name-row">
            <img src={item?.foodType === "VEG" ? vegIcon : nonVegIcon} alt={item?.foodType} className="akm-food-dot" />
            <div className="akm-item-card-name-col">
              <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                <span className="akm-item-card-name">
                  {item?.productName} x{item?.quantity}
                </span>
                {item?.isReturned && <span className="akm-badge recalled">Recalled</span>}
                {item?.isComplimentary && <span className="akm-badge complimentary">Complimentary</span>}
              </div>
              {item?.variantName && (
                <span className="akm-item-card-variant">{item.variantName}</span>
              )}
            </div>
          </div>
          <div className={`akm-item-price-box ${item?.isComplimentary ? "comp-price" : ""}`}>
            {item?.isComplimentary ? (
              <>
                <span className="akm-price-zero">₹0</span>
                <span className="akm-price-strike">₹{formatAmount(item?.originalTotal)}</span>
              </>
            ) : (
              <span className="akm-price-main">₹{formatAmount(item?.subTotal)}</span>
            )}
          </div>
        </div>

        {addonText && <p className="akm-item-card-addons">{addonText}</p>}
        <p className="akm-item-card-kot-label">{kotLabel}</p>
      </div>
    );
  };

  const renderItemCard = (ele, key, kotLabel) => {
    const isRecalled = ele?.productStatus === "RETURNED";
    const isComplimentary = ele?.inventoryStatus === "COMPLEMENTARY";
    const addonText = renderAddonText(ele?.addonProduct);

    return (
      <div className={`akm-item-card ${isRecalled ? "recalled" : ""} ${isComplimentary ? "complimentary" : ""}`} key={key}>
        {/* Top row: name + price */}
        <div className="akm-item-card-top">
          <div className="akm-item-card-name-row">
            <img src={ele?.foodType === "VEG" ? vegIcon : nonVegIcon} alt={ele?.foodType} className="akm-food-dot" />
            <div className="akm-item-card-name-col">
              <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                <span className="akm-item-card-name">
                  {ele?.productName} x{ele?.quantity}
                </span>
                {isRecalled && <span className="akm-badge recalled">Recalled</span>}
                {isComplimentary && <span className="akm-badge complimentary">Complimentary</span>}
              </div>
              {ele?.variantName && (
                <span className="akm-item-card-variant">{ele.variantName}</span>
              )}
            </div>
          </div>
          <div className={`akm-item-price-box ${isComplimentary ? "comp-price" : ""}`}>
            {isComplimentary ? (
              <>
                <span className="akm-price-zero">₹0</span>
                <span className="akm-price-strike">₹{ele?.sellingPrice ?? ele?.subTotal ?? ""}</span>
              </>
            ) : (
              <span className="akm-price-main">₹{ele?.subTotal ?? ""}</span>
            )}
          </div>
        </div>

        {/* Addons */}
        {addonText && <p className="akm-item-card-addons">{addonText}</p>}

        {/* Note */}
        {ele?.comments && (
          <p className="akm-item-card-note">Note: {ele.comments}</p>
        )}

        {/* KOT source label */}
        {kotLabel && <p className="akm-item-card-kot-label">{kotLabel}</p>}

        {getItemTiming(ele)}

        {/* Action buttons */}
        <div className="akm-item-card-actions">
          {/* Complimentary / Remove Complimentary */}
          {onComplimentary && (
            <button
              className={`akm-action-btn ${isComplimentary ? "remove-comp" : "comp"}`}
              onClick={() => {
                if (isRecalled || revertRequestRef.current || serveRequestRef.current || complementaryButtonLoader) return;
                if (isComplimentary) {
                  handleRemoveComplimentary(ele);
                  return;
                }
                setComplimentaryLoadingId(ele?.cartProductId);
                onComplimentary(ele?.cartProductId, ele?.quantity);
              }}
              disabled={
                isRecalled ||
                revertLoadingId !== null ||
                serveLoadingId !== null ||
                (isComplimentary && complementaryButtonLoader) ||
                (complimentaryLoadingId === ele?.cartProductId && complementaryButtonLoader)
              }
              aria-busy={revertLoadingId === ele?.cartProductId}
            >
              {revertLoadingId === ele?.cartProductId || (complimentaryLoadingId === ele?.cartProductId && complementaryButtonLoader)
                ? <CircularProgress size={11} aria-label={isComplimentary ? "Removing complimentary" : "Adding complimentary"} sx={{ color: "inherit" }} />
                : isComplimentary ? "Remove complimentary" : "Complimentary"}
            </button>
          )}

          {/* Discount */}
          {onDiscount && !isRecalled && (
            <button className="akm-action-btn discount" onClick={() => onDiscount(ele)}>
              Discount
            </button>
          )}

          {/* Recall / Recall logged */}
          {isRecalled ? (
            <button className="akm-action-btn recall-logged" disabled>Recall logged</button>
          ) : (
            <button
              className="akm-action-btn recall"
              onClick={() => setRecallModalData({
                cartProductId: ele?.cartProductId,
                productTitle: ele?.productName,
                cartQuantity: ele?.quantity,
                foodType: ele?.foodType,
              })}
            >
              Recall
            </button>
          )}

          {/* Repeat order */}
          <button
            className="akm-action-btn repeat"
            onClick={() => handleRepeatOrder(ele)}
            disabled={repeatLoadingId === ele?.cartProductId}
          >
            {repeatLoadingId === ele?.cartProductId
              ? <CircularProgress size={11} sx={{ color: "inherit" }} />
              : "Repeat order"}
          </button>
          <button
            type="button"
            className="akm-action-btn serve"
            aria-label={ele?.productStatus === "SERVED" ? "Served" : "Serve"}
            aria-busy={serveLoadingId === ele?.cartProductId}
            onClick={() => handleServe(ele)}
            disabled={ele?.productStatus === "SERVED" || !isReadyToServe(ele) ||
              serveLoadingId !== null || revertLoadingId !== null || complementaryButtonLoader}
          >
            {serveLoadingId === ele?.cartProductId && (
              <CircularProgress size={11} aria-label="Serving item" sx={{ color: "inherit" }} />
            )}
            {ele?.productStatus === "SERVED" ? "Served" : "Serve"}
          </button>
        </div>
      </div>
    );
  };

  return (
    <>
      <Modal open={open} onClose={onClose}>
        <Box className="akm-drawer" sx={{
          position: "fixed", top: 0, right: 0, height: "100%",
          width: "80vw",
          borderRadius: 0, display: "flex",
          flexDirection: "column", overflow: "hidden",
          boxShadow: "-4px 0 32px rgba(0,0,0,0.18)", p: 0,
          animation: "akmSlideIn 0.25s ease",
        }}>
          <KitchenClockProvider active={open}>
          {/* Header */}
          <div className="akm-header">
            <div className="akm-header-left">
              <span className="akm-title">All KOTs ({kotGroups?.length})</span>
              <div className="akm-list-toggle" onClick={() => setIsListView((v) => !v)}>
                <span className="akm-toggle-label">List View</span>
                <div className={`akm-toggle-switch ${isListView ? "on" : ""}`}>
                  <div className="akm-toggle-knob" />
                </div>
              </div>
            </div>
            <button className="akm-close-btn" onClick={onClose}>
              <XMarkIcon style={{ width: 20, height: 20 }} />
            </button>
          </div>
          <p className="akm-subtitle">
            All kitchen orders for this table. Actions are grouped into compact cards so discounts, recalls, and complimentary changes are easier to scan.
          </p>

          {/* Body */}
          <div className="akm-body">
            {kitchenJobsStatus === "loading" && (
              <div className="akm-kitchen-notice loading" role="status">
                <span className="akm-kitchen-notice-mark" aria-hidden="true" />
                <span>Loading kitchen status…</span>
              </div>
            )}
            {kitchenJobsStatus === "error" && (
              <div className="akm-kitchen-notice error" role="alert">
                <div>
                  <strong>Kitchen status unavailable</strong>
                  <span>{kitchenJobsError || "Current kitchen updates could not be loaded. Item statuses are hidden until they can be refreshed."}</span>
                </div>
                <button type="button" onClick={refreshKitchenJobs} className="akm-kitchen-retry">Retry</button>
              </div>
            )}
            {kotGroups?.length === 0 ? (
              <p className="akm-empty">No KOTs placed yet.</p>
            ) : isListView ? (
              <div className="akm-list-view">
                {aggregatedKotItems.map((item, idx) => renderAggregatedCard(item, idx))}
              </div>
            ) : (
              kotGroups?.map((group, idx) => {
                const firstItem = group?.items?.[0];
                const waiterName = firstItem?.modifiedByName || firstItem?.modifiedBy || "";
                const avgPrepTime = firstItem?.avgPrepTime || group?.avgPrepTime || null;
                const timeStr = getOrderedTimeText(firstItem);
                const expanded = isExpanded(idx);
                const groupTotal = group?.items?.reduce((sum, item) =>
                  item?.productStatus === "RETURNED" ? sum : sum + parseFloat(item?.subTotal || 0), 0) || 0;

                return (
                  <div className="akm-group" key={`kot-group-${group?.sequence}`}>
                    {/* Group header */}
                    <div className="akm-group-header" onClick={() => toggleGroup(idx)}>
                      <div className="akm-group-header-content">
                        <div className="akm-group-header-top">
                          <span className="akm-group-title">Order No. {group?.sequence}: {timeStr}</span>
                          <div className="akm-group-header-right">
                            {expanded
                              ? <ChevronUpIcon style={{ width: 18, height: 18, color: "#6b7280" }} />
                              : <ChevronDownIcon style={{ width: 18, height: 18, color: "#6b7280" }} />}
                          </div>
                        </div>
                        <div className="akm-group-meta-row">
                          <ClockIcon style={{ width: 13, height: 13, color: "#9ca3af" }} />
                          {avgPrepTime && <span className="akm-group-meta">Avg prep: {formatPrepEstimate(avgPrepTime)}</span>}
                          {waiterName && <span className="akm-group-meta">{waiterName}</span>}
                        </div>
                      </div>
                    </div>

                    {/* Items */}
                    {expanded && (
                      <div className="akm-group-body">
                        {group?.items?.map((ele) => renderItemCard(ele, ele?.cartProductId))}

                        {/* Group footer */}
                        <div className="akm-group-footer">
                          <div className="akm-order-status">
                            <span className="akm-status-dot" />
                            <span className="akm-status-text">Order placed</span>
                          </div>
                          <div className="akm-group-footer-right">
                            <span className="akm-group-footer-total">₹{groupTotal.toFixed(0)}</span>
                            <button
                              className="akm-reprint-btn"
                              onClick={() => handleReprintKot(group?.sequence)}
                              disabled={reprintLoadingSeq === group?.sequence}
                            >
                              {reprintLoadingSeq === group?.sequence
                                ? <CircularProgress size={12} sx={{ color: "#374151" }} />
                                : <><PrinterIcon style={{ width: 14, height: 14 }} /> Reprint KOT</>}
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>

          </KitchenClockProvider>
        </Box>
      </Modal>

      {recallModalData && (
        <RecallOrderModal
          recallOrderModalOpen={!!recallModalData}
          setRecallOrderModalOpen={(val) => { if (!val) setRecallModalData(null); }}
          productTitle={recallModalData.productTitle}
          cartQuantity={recallModalData.cartQuantity}
          cartProductId={recallModalData.cartProductId}
          foodType={recallModalData.foodType}
          setShowFineDineActionButtons={setShowFineDineActionButtons}
          setHasRequestedForNewItem={setHasRequestedForNewItem}
        />
      )}
    </>
  );
};

export default AllKotsModal;
